import { HTMLAttributes, MouseEventHandler, ReactNode, useCallback } from 'react';
import classNames from 'classnames';
import Icon from 'components/atoms/Icon';
import { IconName } from 'components/atoms/Icon/all';
import { ButtonSize, ButtonStyleType } from 'utils/frontendTypes';
import Loader from 'components/molecules/Loader';
import { isFunction } from 'utils/common';
import { twMerge } from 'tailwind-merge';

export interface ButtonProps extends HTMLAttributes<HTMLButtonElement> {
  text?: ReactNode;
  onClick?: any;
  isSelected?: boolean;
  disabled?: boolean;
  icon?: IconName;
  dataDurableId?: string;
  hideHighlight?: boolean;
  iconPosition?: 'left' | 'right' | 'center';
  type?: ButtonStyleType;
  size?: ButtonSize;
  children?: ReactNode;
  form?: string;
  isLoading?: boolean;
  iconSize?: number;
  iconClassName?: string;
  noBubble?: boolean;
  autoFocus?: boolean;
  buttonType?: 'button' | 'submit' | 'reset';
}

function Button({
  text,
  icon,
  iconPosition = 'left',
  type,
  onClick,
  isSelected = false,
  disabled,
  className,
  dataDurableId,
  children,
  iconSize,
  isLoading,
  iconClassName,
  hideHighlight,
  size,
  noBubble,
  buttonType,
  autoFocus,
  ...rest
}: ButtonProps) {
  const variantClasses = () => {
    switch (type) {
      /**
       *
       * These match the latest gloo-ui button styles
       *
       */
      case 'primary':
        return 'bg-primary hover:bg-opacity-80 text-white border border-primary';
      case 'secondary':
        return 'text-white bg-charcoal-800 hover:bg-charcoal-700 focus:bg-charcoal-700 border border-charcoal-800';
      case 'tertiary':
        return 'text-charcoal-800 bg-transparent hover:bg-charcoal-100 focus:bg-charcoal-100 border border-charcoal-300';
      case 'white':
        return 'bg-white hover:bg-opacity-80 text-charcoal-800 border border-charcoal-200';
      case 'destruction':
        return 'bg-red-700 text-white hover:bg-red-800 focus:bg-red-800 border border-red-200';
      case 'transparent':
        return 'bg-transparent hover:bg-transparent hover:text-primary-600 text-charcoal-800 py-0 px-0 rounded-none hover:shadow-none';
      /**
       *
       * These are the old button styles
       *
       */
      case 'black':
        return 'bg-glooCharcoal hover:bg-opacity-80 text-white hover:text-white border border-glooCharcoal';
      case 'navy':
        return 'bg-glooNavy-700 hover:bg-opacity-80 text-white hover:text-white border border-glooNavy-700';
      case 'gray-toggle':
        return 'bg-thryveGray text-white';
      case 'icon-only':
        return 'bg-transparent hover:bg-transparent hover:text-blue text-thryveGray py-1 px-2 font-medium';
    }
  };

  const sizeClasses = () => {
    switch (size) {
      case 'xs':
        return 'py-1 px-2 text-xs';
      case 'small':
        return 'py-1 px-3';
      case 'no-padding':
        return 'py-0 px-0';
      case 'gloo-medium':
        return 'px-9 py-2.5';
      default:
        return 'py-2 px-4';
    }
  };

  const buttonClasses = twMerge(
    'inline-flex items-center font-medium rounded-sm hover:shadow-sm',
    classNames({
      'focus:ring': !hideHighlight,
      'py-0 px-0 rounded-none block': isSelected,
      'border-b-2 border-blue text-blue': isSelected && type !== 'icon-only',
    }),
    !isSelected && type !== 'transparent' && sizeClasses(),
    variantClasses(),
    disabled && type === 'transparent'
      ? 'text-glooMistyGray hover:text-glooMistyGray'
      : disabled
        ? 'text-white hover:text-white bg-glooMistyGray border-glooMistyGray hover:bg-opacity-100 hover:bg-glooMistyGray'
        : '',
    className,
  );

  const renderIcon = () => {
    return (
      <Icon
        name={icon}
        size={iconSize || (iconPosition === 'left' || iconPosition === 'right' || size === 'small' ? 17 : 24)}
        className={classNames(
          'py-1',
          {
            'pr-2': text && iconPosition === 'left',
            'pl-2': text && iconPosition === 'right',
          },
          iconClassName,
        )}
        svgFillClassName={type === 'icon-only' && isSelected ? 'text-thryveGray fill-current' : ''}
      />
    );
  };

  const handleClick = useCallback(
    (e: Parameters<MouseEventHandler<HTMLButtonElement>>[0]) => {
      if (noBubble) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (isFunction(onClick)) onClick(e);
    },
    [onClick, noBubble],
  );

  return (
    <button
      type={buttonType}
      data-durable-id={dataDurableId}
      data-intercom-target={dataDurableId}
      disabled={isLoading || disabled}
      className={buttonClasses}
      autoFocus={autoFocus}
      {...rest}
      onClick={onClick ? handleClick : undefined}
    >
      {isLoading ? (
        <div>
          <Loader color={(['tertiary', 'transparent'] as ButtonStyleType[]).includes(type) ? 'black' : 'white'} />
        </div>
      ) : (
        <>
          {children}
          {icon && text && iconPosition === 'left' && renderIcon()}
          {text}
          {icon && (iconPosition === 'center' || !text) && renderIcon()}
          {icon && text && iconPosition === 'right' && renderIcon()}
        </>
      )}
    </button>
  );
}

export default Button;
